/**
 * Basic modal dialog model/view implementation
 * Simple Usage:
   var createReport = new CreateReportView();
   var exportDialog = new Dialog({
        title: "Request Export",
        buttons: [{
            "role": "default",
            "dismiss": true,
            "caption": "Close"
        }, {
            "role": "primary",
            "caption": "Create"
        }],
        content: createReport,
        render: true
    });
 */
import { isValidElement } from "react";
import { View, ReactToMarionetteView } from "extensions/marionette";
import { CollectionView } from "backbone.marionette";

import { _ } from "extensions/underscore";

import { emitter } from "common/emitter";

import dialogTemplate from "./dialog.jst";

import "./dialog.scss";

export const Dialog = View.extend({
    template: dialogTemplate,
    className: "modal fade pz-dialog",
    attributes: {
        tabindex: -1,
        role: "dialog"
    },
    events: {
        "click button": "clickButton"
    },
    shouldFocusPrimaryButton: false,
    
    initialize() {
        if (this.getOption("render")) {
            this.render();
        }
    
        emitter.on("logout:before", this.dismiss);
    },
    /**
     * Namespace a string with view's unique id
     * @param {string} eventName event name to be namespaced
     * @returns {string}
     */
    namespaceEvent(eventName) {
        return [eventName, this.cid].join(".");
    },
    
    /**
     * Puts unique identifiers for dialog's buttons to properly capture user-generated events,
     * i.e. One simply doesn't rely on the DOM to match elements to their label in instance :)
     */
    prepareButtons() {
        const buttons = this.getOption("buttons") || [];
        
        this.buttons = buttons.map((button) => {
            button.cid = _.uniqueId("c");
            return button;
        });
    },
    
    serializeData() {
        this.prepareButtons();
        
        return {
            title: this.getOption("title") || "",
            content: this.getOption("content") || "",
            buttons: this.buttons,
            footnote: this.getOption("footnote"),
            dismissable: this.getOption("dismissable") || true
        };
    },
    
    onRender() {
        const opts = _.defaults({
            backdrop: this.getOption("backdrop"),
            keyboard: this.getOption("dismissable"),
            show: this.getOption("show"),
            parent: this.getOption("parent")
        }, {
            backdrop: true,
            keyboard: true,
            show: true,
            parent: document.body
        });
        this._show(opts);
        this.onRenderContent();
        
        if (this.getOption("shouldFocusPrimaryButton")) {
            this.focusPrimaryButton();
        }
    },

    getContent() {
        return this.getChildView("content");
    },
    
    onRenderContent() {
        // Add content region to follow layoutView behavior
        const region = this.addRegion("content", ".modal-body");
        const content = this.getOption("content");
        
        if (content) {
            let view;
            if (isValidElement(content)) {
                view = new ReactToMarionetteView({
                    component: content
                });
            } else if ([View, CollectionView].some((constructr) => content instanceof constructr)) {
                view = content;
            } else if (content) {
                view = new View({
                    template: content
                });
            }
            view.parent = this;
            region.show(view);
        }
    },
    /**
     * Automatically focus on the primary button so users can hit [Enter] to submit
     */
    focusPrimaryButton() {
        const primaryButton = this.buttons.find((b) => b.role === "primary");
        
        if (primaryButton) {
            const $button = this.$(`button[data-cid='${primaryButton.cid}']`);
            this.$el.on("shown.bs.modal", () => {
                $button.focus();
            });
        }
    },
    
    /**
     * Append dialog to parent, bind hide event, and call internal Bootstrap's method's to display it
     * @param opts rendering options
     * @private
     */
    _show(opts) {
        const noop = () => {};
        const onShow = this.getOption("onShow") || noop;
        const onShown = this.getOption("onShown") || noop;
        const onHide = this.getOption("onHide") || noop;
        
        this.$el.appendTo(opts.parent);
        
        // When Dialog is finally hidden, destroy it to release memeory
        this.$el.on("hidden.bs.modal", this.destroy.bind(this));
        
        // Execute attached callbacks if any
        this.$el.on("show.bs.modal", onShow);
        this.$el.on("shown.bs.modal", (...args) => {
            onShown(...args);
            const contentRegion = this.getRegion("content");
            if (contentRegion && contentRegion.hasView()) {
                this.getChildView("content").triggerMethod("shown");
            }
        });
        this.$el.on("hide.bs.modal", onHide);
        this.$el.modal(_.omit(opts, "parent"));
    },
    
    /**
     * Programmatically dismiss the modal
     */
    dismiss() {
        this?.$el?.modal("hide");
    },
    
    /**
     * Clean up the ItemView
     */
    onDestroy() {
        // Clean up all modal events
        emitter.off("logout:before", this.dismiss);
        this.$el.off(".modal");
    },
    
    /**
     * Handler for button clicks
     * @param event
     */
    clickButton(event) {
        event.preventDefault();
        
        // Get instance buttons
        const button = this.buttons.find((b) => b.cid === event.currentTarget.dataset.cid);
        
        if (button && button.onClick) {
            button.onClick.call(this, event);
        }
    }
}, {
    show(...args) {
        const Constructor = this;
        return new Constructor(...args);
    }
});
